import { Suggestion } from "@b2bportal/air-shopping-api";
import { IPassengerCounts, TripCategory } from "@hopper-b2b/types";
import dayjs from "dayjs";
import { withPlatformLocale } from "./langUtils";
import {
  AirPlatformLocationType,
  serializeMultiCityLegs,
} from "./multiCityLegs";
import { PATH_FLIGHTS_SHOP_PLATFORM } from "./urlPaths";

/** Whether a suggestion names an airport or a city, which its bare code cannot say. */
export const getLocationType = (
  suggestion?: Suggestion
): AirPlatformLocationType =>
  suggestion?.id?.Id === "Flight" && suggestion.id.code.regionType === "city"
    ? "City"
    : "Airport";

/** The IATA or city code of a flight suggestion, or `undefined` for any other kind. */
export const getLocationCode = (suggestion?: Suggestion): string | undefined =>
  suggestion?.id?.Id === "Flight" ? suggestion.id.code.code : undefined;

/** Two suggestions name the same place only when both code and kind match. */
export const isSameLocation = (a?: Suggestion, b?: Suggestion): boolean => {
  const codeA = getLocationCode(a);
  return (
    codeA !== undefined &&
    codeA === getLocationCode(b) &&
    getLocationType(a) === getLocationType(b)
  );
};

const requireLocationCode = (
  suggestion: Suggestion,
  role: "origin" | "destination"
): string => {
  const code = getLocationCode(suggestion);
  if (code === undefined) {
    throw new Error(`Multi-city leg ${role} is not a flight location`);
  }
  return code;
};

const PLATFORM_DATE_FORMAT = "YYYY-MM-DD";

/**
 * Passenger counts as the platform expects them. Zero counts are omitted so the
 * URL matches what the platform's own search bar produces.
 */
const buildPassengerParams = (
  pax: IPassengerCounts
): Record<string, string> => ({
  adults: (pax.adultsCount ?? 1).toString(),
  ...(pax.childrenCount ? { children: pax.childrenCount.toString() } : {}),
  ...(pax.infantsInSeatCount
    ? { seatedInfants: pax.infantsInSeatCount.toString() }
    : {}),
  ...(pax.infantsOnLapCount
    ? { onLapInfants: pax.infantsOnLapCount.toString() }
    : {}),
});

const buildShopUrl = (searchParams: URLSearchParams): string =>
  withPlatformLocale(
    `/${PATH_FLIGHTS_SHOP_PLATFORM}?${searchParams.toString()}`
  );

export type AirPlatformQueryBuilderProps = {
  origin?: Suggestion;
  destination?: Suggestion;
  departureDate: Date;
  returnDate?: Date;
  tripCategory: TripCategory;
  pax: IPassengerCounts;
};

/**
 * Builds the platform shop URL for a one-way or round-trip search.
 *
 * Throws for multi-city: that search has a different shape (`legs`, no `step`)
 * and goes through {@link airPlatformMultiCityQueryBuilder}.
 */
export const airPlatformQueryBuilder = ({
  origin,
  destination,
  departureDate,
  returnDate,
  tripCategory,
  pax,
}: AirPlatformQueryBuilderProps): string => {
  if (tripCategory === TripCategory.MULTI_CITY) {
    throw new Error(
      "airPlatformQueryBuilder does not take multi-city searches; use airPlatformMultiCityQueryBuilder"
    );
  }

  const originCode = getLocationCode(origin);
  const destinationCode = getLocationCode(destination);
  const isOneWay = tripCategory === TripCategory.ONE_WAY;

  const searchParams = new URLSearchParams({
    tripType: isOneWay ? "one-way" : "round-trip",
    departureDate: dayjs(departureDate).format(PLATFORM_DATE_FORMAT),
    ...(returnDate
      ? { returnDate: dayjs(returnDate).format(PLATFORM_DATE_FORMAT) }
      : {}),
    ...(originCode
      ? {
          origin: originCode,
          originType: getLocationType(origin),
        }
      : {}),
    ...(destinationCode
      ? {
          destination: destinationCode,
          destinationType: getLocationType(destination),
        }
      : {}),
    ...buildPassengerParams(pax),
    step: "outbound",
  });

  return buildShopUrl(searchParams);
};

export interface AirPlatformMultiCityLeg {
  origin: Suggestion;
  destination: Suggestion;
  departureDate: Date;
}

export type AirPlatformMultiCityQueryBuilderProps = {
  legs: readonly AirPlatformMultiCityLeg[];
  pax: IPassengerCounts;
};

/**
 * Builds the platform shop URL for a multi-city search.
 *
 * Multi-city has no outbound/return step, so unlike the single-leg builder this
 * never writes `step`. The platform's route schema rejects it on that branch.
 *
 * Throws when a leg location is not a flight suggestion: the search form validates
 * every leg before submit, so reaching this without a code is a bug.
 */
export const airPlatformMultiCityQueryBuilder = ({
  legs,
  pax,
}: AirPlatformMultiCityQueryBuilderProps): string => {
  const serializedLegs = serializeMultiCityLegs(
    legs.map((leg) => ({
      origin: requireLocationCode(leg.origin, "origin"),
      originType: getLocationType(leg.origin),
      destination: requireLocationCode(leg.destination, "destination"),
      destinationType: getLocationType(leg.destination),
      departureDate: dayjs(leg.departureDate).format(PLATFORM_DATE_FORMAT),
    }))
  );

  const searchParams = new URLSearchParams({
    tripType: "multi-city",
    legs: serializedLegs,
    ...buildPassengerParams(pax),
  });

  return buildShopUrl(searchParams);
};
